import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaArrowRight } from 'react-icons/fa';

const Services = () => {
    const [currency, setCurrency] = useState('GBP');

    // Exchange rates (updated periodically - in production, use live API)
    const exchangeRates = {
        GBP: 1,
        AED: 4.75,  // 1 GBP = 4.75 AED
        SAR: 4.85,  // 1 GBP = 4.85 SAR
        KWD: 0.40,  // 1 GBP = 0.40 KWD
        QAR: 4.72,  // 1 GBP = 4.72 QAR
    };

    const currencySymbols = {
        GBP: '£',
        AED: 'AED ',
        SAR: 'SAR ',
        KWD: 'KWD ',
        QAR: 'QAR ',
    };

    const convertPrice = (priceGBP) => {
        const numericPrice = parseInt(priceGBP.replace('£', ''));
        const converted = (numericPrice * exchangeRates[currency]).toFixed(0);
        return `${currencySymbols[currency]}${converted}`;
    };

    const courses = [
        {
            icon: "✏️",
            title: "11+ Exam Preparation",
            description: "Comprehensive preparation covering verbal reasoning, non-verbal reasoning, English, and mathematics.",
            price: "£45",
            pricePerHour: "per hour",
            differentiation: [
                "AI-powered diagnostic assessment",
                "Personalized study plan within 24 hours",
                "Weekly progress reports to parents"
            ],
            features: [
                "Live online classes",
                "Personalized learning plan",
                "Expert feedback",
                "Progress tracking"
            ],
            popular: false
        },
        {
            icon: "📚",
            title: "GCSE Comprehensive Support",
            description: "Targeted support for all major GCSE subjects, focusing on core concepts, exam technique, and revision strategies.",
            price: "£55",
            pricePerHour: "per hour",
            differentiation: [
                "Exam board-specific content (AQA, Edexcel, OCR)",
                "1-on-1 sessions with Oxbridge graduates"
            ],
            features: [
                "All Core Subjects",
                "Exam Board Specific",
                "Revision & Past Papers",
                "Grade Improvement"
            ],
            popular: true
        },
        {
            icon: "🏛️",
            title: "Advanced A-Level Support",
            description: "Advanced tuition from subject specialists, designed to deepen understanding and secure top university places.",
            price: "£65",
            pricePerHour: "per hour",
            differentiation: [
                "UCAS personal statement review included",
                "Subject-specific mentors from top universities"
            ],
            features: [
                "In-depth Subject Knowledge",
                "UCAS & Personal Statement",
                "Advanced Problem Solving",
                "Exam Preparation"
            ],
            popular: false
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 40, scale: 0.95 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1]
            }
        }
    };

    return (
        <section id="services" className="section">
            <motion.div
                className="section-header"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
                <h2 className="section-title">Explore Our Services</h2>
                <p className="section-subtitle">
                    Choose from a wide range of services designed to help you excel in your studies.
                </p>

                {/* Currency Toggle */}
                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {Object.keys(exchangeRates).map((curr) => (
                        <button
                            key={curr}
                            onClick={() => setCurrency(curr)}
                            style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '8px',
                                border: currency === curr ? '2px solid var(--color-primary)' : '1px solid #ddd',
                                background: currency === curr ? 'var(--color-teal-light)' : 'white',
                                color: currency === curr ? 'var(--color-primary)' : '#666',
                                fontWeight: currency === curr ? '600' : 'normal',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontSize: '0.9rem'
                            }}
                        >
                            {curr}
                        </button>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="services-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
            >
                {courses.map((course, index) => (
                    <motion.div
                        key={index}
                        className={`service-card ${course.popular ? 'service-card-popular' : ''}`}
                        variants={cardVariants}
                        whileHover={{
                            y: -8,
                            transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
                        }}
                    >
                        {course.popular && (
                            <motion.div
                                className="service-badge"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                            >
                                Most Popular
                            </motion.div>
                        )}
                        <div className="service-icon">{course.icon}</div>
                        <h3>{course.title}</h3>
                        <p>{course.description}</p>
                        <div className="service-price">
                            <span style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                                {convertPrice(course.price)}
                            </span>
                            <span className="price-per-hour" style={{ marginLeft: '0.5rem', fontSize: '1rem', color: '#666', fontWeight: 'normal' }}>
                                {course.pricePerHour}
                            </span>
                        </div>
                        <div className="service-differentiation">
                            <h4>What Makes Us Different:</h4>
                            <ul className="service-features">
                                {course.differentiation.map((item, idx) => (
                                    <li key={idx}>
                                        <FaCheckCircle className="feature-icon" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <a href="#contact" className="service-button">
                            Buy Now <FaArrowRight />
                        </a>
                    </motion.div>
                ))}
            </motion.div>

            <motion.div
                className="section-cta"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
            >
                <a href="#contact" className="button-primary-yellow">
                    View All Services
                </a>
            </motion.div>
        </section>
    );
};

export default Services;
